/**
 * Billing Profile API client — F-002 Phase 4 (IMPL-P4-001).
 *
 * Typed fetch wrappers for `/api/v1/billing-profile/*`:
 *  - GET    /api/v1/billing-profile          → BillingProfileView | null (404 = absent)
 *  - PUT    /api/v1/billing-profile          → BillingProfileView (upsert)
 *  - DELETE /api/v1/billing-profile/iban     → BillingProfileView (clear IBAN)
 *
 * Conventions mirror `apps/web/src/features/auth/lib/auth-client.ts`:
 *  - `credentials: 'include'` so the Better Auth session cookie travels with
 *    every request.
 *  - Errors thrown by these helpers carry `status` + `code` fields, lifted
 *    from the API JSON envelope (`{ code, message }`).
 *  - Inputs are typed via the shared Zod-inferred types from `@app/shared`.
 *
 * Note: until F-002 Phase 3 lands the API contract for the response payload,
 * `BillingProfileView` is declared locally and mirrors the server-side row
 * with the IBAN already redacted (`ibanMasked` only — never the plaintext
 * IBAN). When the API publishes the shared type, this file should be updated
 * to import it from `@app/shared`.
 */
import type { BillingProfileUpsertInput, PaymentMethodPreference } from '@app/shared';

const API = '/api/v1/billing-profile';

/**
 * Server-redacted projection of a BillingProfile row exposed to the client.
 *
 * The plaintext IBAN is NEVER sent to the SPA — only `ibanMasked` (e.g.
 * "IT60****WXYZ"). All anagrafica + indirizzo fields are nullable so the
 * client can render a partially-completed profile and the badge can compute
 * `isBillingProfileComplete()` against it.
 */
export interface BillingProfileView {
  id: string;
  type: 'PF' | 'PG' | null;
  // PF
  firstName: string | null;
  lastName: string | null;
  codiceFiscale: string | null;
  // PG
  ragioneSociale: string | null;
  piva: string | null;
  sdiCode: string | null;
  sdiPec: string | null;
  // Indirizzo (comune)
  indirizzo: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  nazione: string | null;
  // Pagamento
  paymentMethodPreference: PaymentMethodPreference | null;
  // IBAN (masked only — plaintext lives server-side cifrato)
  ibanMasked: string | null;
  // Stato
  fiscalReverificationAt: string | null;
  lastCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export class ProfiloApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ProfiloApiError';
    this.status = status;
    this.code = code;
  }
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  return (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
}

/**
 * GET /api/v1/billing-profile
 *
 * Returns the current account's billing profile, or `null` if the API
 * responds 404 (no profile yet — typical first-time visit).
 */
export async function getBillingProfile(): Promise<BillingProfileView | null> {
  const res = await fetch(API, { credentials: 'include' });

  if (res.status === 404) return null;
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ProfiloApiError(
      res.status,
      env.code ?? 'GET_FAILED',
      env.message ?? 'Impossibile caricare il profilo',
    );
  }

  return (await res.json()) as BillingProfileView;
}

/**
 * PUT /api/v1/billing-profile
 *
 * Upsert dei dati anagrafici/indirizzo/SDI (e IBAN plaintext opzionale che
 * il server cifra prima di persistere). Risposta: profilo aggiornato.
 */
export async function upsertBillingProfile(
  input: BillingProfileUpsertInput,
): Promise<BillingProfileView> {
  const res = await fetch(API, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ProfiloApiError(
      res.status,
      env.code ?? 'UPSERT_FAILED',
      env.message ?? 'Impossibile salvare il profilo',
    );
  }

  return (await res.json()) as BillingProfileView;
}

/**
 * DELETE /api/v1/billing-profile/iban
 *
 * Rimuove l'IBAN cifrato dal profilo. Richiede `{ confirm: true }` body
 * lato server per evitare cancellazioni accidentali.
 */
export async function clearIban(): Promise<BillingProfileView> {
  const res = await fetch(`${API}/iban`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: true }),
  });

  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ProfiloApiError(
      res.status,
      env.code ?? 'CLEAR_IBAN_FAILED',
      env.message ?? "Impossibile rimuovere l'IBAN",
    );
  }

  return (await res.json()) as BillingProfileView;
}

export function isProfiloApiError(err: unknown): err is ProfiloApiError {
  return err instanceof ProfiloApiError;
}
